import { useCallback, useState } from "react";

/**
 * Shared hook for managing expandable resource tables with nested resources
 * Used by both ApplicationsTable and MspApplicationsTable
 */
export function useExpandableResourceTable<TResource extends { id: string }>(
  fetchNestedResources: (
    resourceGroupId: string,
  ) => Promise<{ resources: TResource[] } | null>,
) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [expandedRowsData, setExpandedRowsData] = useState<
    Map<string, TResource[]>
  >(new Map());

  const toggleRowExpansion = useCallback(
    async (resourceId: string, resourceGroupId?: string) => {
      setExpandedRows((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(resourceId)) {
          // Collapse: remove from expanded set and clear data
          newSet.delete(resourceId);
          setExpandedRowsData((prevData) => {
            const newMap = new Map(prevData);
            newMap.delete(resourceId);
            return newMap;
          });
        } else {
          // Expand: fetch nested resources
          newSet.add(resourceId);
          if (resourceGroupId) {
            fetchNestedResources(resourceGroupId)
              .then((result) => {
                if (result?.resources) {
                  setExpandedRowsData((prevData) => {
                    const newMap = new Map(prevData);
                    newMap.set(resourceId, result.resources);
                    return newMap;
                  });
                }
              })
              .catch((error) => {
                console.error("Failed to fetch nested resources:", error);
                setExpandedRows((current) => {
                  const newSet = new Set(current);
                  newSet.delete(resourceId);
                  return newSet;
                });
              });
          }
        }
        return newSet;
      });
    },
    [fetchNestedResources],
  );

  const removeFromExpandedData = useCallback(
    (resourceId: string, parentResourceId: string) => {
      setExpandedRowsData((prevData) => {
        const newMap = new Map(prevData);
        const currentNestedResources = newMap.get(parentResourceId) || [];
        const updatedNestedResources = currentNestedResources.filter(
          (r) => r.id !== resourceId,
        );

        if (updatedNestedResources.length > 0) {
          newMap.set(parentResourceId, updatedNestedResources);
        } else {
          newMap.delete(parentResourceId);
          setExpandedRows((prev) => {
            const newSet = new Set(prev);
            newSet.delete(parentResourceId);
            return newSet;
          });
        }

        return newMap;
      });
    },
    [],
  );

  const addToExpandedData = useCallback(
    (resource: TResource, targetResourceId: string) => {
      setExpandedRowsData((prevData) => {
        const newMap = new Map(prevData);
        const currentTargetNestedResources =
          newMap.get(targetResourceId) || [];
        const updatedTargetNestedResources = [
          ...currentTargetNestedResources,
          resource,
        ];

        newMap.set(targetResourceId, updatedTargetNestedResources);
        return newMap;
      });
    },
    [],
  );

  return {
    expandedRows,
    expandedRowsData,
    toggleRowExpansion,
    removeFromExpandedData,
    addToExpandedData,
    setExpandedRows,
    setExpandedRowsData,
  };
}
